import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PpHttpHeaders, PpHttpParams, PpHttpService } from '@penpencil/common';
import { UserService } from 'app/core/user/user.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { PermissionService } from '../services';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    payload = {
        client_id: 'system-admin',
        client_secret: 'KjPXuAVfC5xbmgreETNMaL7z',
        grant_type: 'password',
    };

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private ppHttpService: PpHttpService,
        private _userService: UserService,
        private _permissionService: PermissionService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('pp-token', token);
    }

    get accessToken(): string {
        return localStorage.getItem('pp-token') ?? '';
    }
    set refreshToken(token: string) {
        localStorage.setItem('pp-refresh-token', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('pp-refresh-token') ?? '';
    }
    set user(userObj) {
        localStorage.setItem('user', JSON.stringify(userObj));
    }

    get user(): Object {
        return JSON.parse(localStorage.getItem('user')) ?? {};
    }

    set expiryDate(exp: number) {
        localStorage.setItem('expires_in', exp.toString());
    }

    get expiryDate(): number {
        return parseInt(localStorage.getItem('expires_in')) ?? 0;
    }

    set randomId(randomId: string) {
        localStorage.setItem('randomId', randomId);
    }

    get randomId() {
        return localStorage.getItem('randomId') ?? '';
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param username
     */
    forgotPassword(username: string): Observable<any> {
        return this.ppHttpService.post(
            { username },
            { apiPath: 'v1/admin/users/forget-password' },
        );
    }

    /**
     * Reset password
     *
     * @param password
     */

    resetPassword(user: any): Observable<any> {
        return this.ppHttpService.post(user, {
            apiPath: 'v1/admin/users/reset-password',
        });
    }

    resendOtp(user: any): Observable<any> {
        return this.ppHttpService.post(
            { mobile: user },
            {
                apiPath: 'v1/admin/users/resend-otp',
                params: new PpHttpParams().append('smsType', 0),
            },
        );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn({
        username,
        password,
        organisationId,
    }: {
        username: string;
        password: string;
        organisationId?: string;
    }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this.ppHttpService
            .post(
                {
                    username,
                    password,
                    ...this.payload,
                    ...(organisationId && {
                        organizationId: organisationId,
                    }),
                },
                {
                    apiPath: 'v1/admin/oauth/user/token',
                    ...(organisationId && {
                        headers: new PpHttpHeaders().append(
                            'client-id',
                            organisationId,
                        ),
                    }),
                },
            )
            .pipe(
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.data?.access_token;
                    this.refreshToken = response.data?.refresh_token;
                    //Expiry date from response
                    this.expiryDate = new Date().setUTCMilliseconds(
                        response.data.expires_in,
                    );
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.data.user;
                    this.user = response.data.user;

                    // Return a new observable with the response
                    return of(response);
                }),
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient
            .post('api/auth/refresh-access-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false),
                ),
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;
                    this.refreshToken = response.data?.refresh_token;
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                }),
            );
    }

    googleAuthSignIn(data: any, clientId?: string): Observable<any> {
        return this.ppHttpService
            .post(data, {
                apiPath: 'v3/admin/oauth/social-connect',
                ...(clientId && {
                    headers: new PpHttpHeaders().append('client-id', clientId),
                }),
            })
            .pipe(
                tap((res: any) => {
                    // Store the access token in the local storage
                    this.accessToken = res.data?.access_token;
                    this.refreshToken = res.data?.refresh_token;
                    //Expiry date from response
                    this.expiryDate = new Date().setUTCMilliseconds(
                        res.data.expires_in,
                    );
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = res.data.user;
                    this.user = res.data.user;
                }),
                // catchError(e => of(e))
            );
    }

    /**
     * Sign out
     */
    signOut(): void {
        try {
            this.ppHttpService
                .post(
                    { deviceId: '' },
                    {
                        apiPath: 'v1/admin/oauth/logout',
                    },
                )
                .subscribe(
                    () => {
                        this._permissionService.setPermissions([], true);

                        // Remove the access token from the local storage
                        localStorage.clear();
                        // Set the authenticated flag to false
                        this._authenticated = false;
                    },
                    (error) => {
                        console.error('Error during sign out:', error);
                        localStorage.clear();
                        this._authenticated = false;
                    },
                );
        } catch (error) {
            console.error('Unexpected error during sign out:', error);
            localStorage.clear();
            this._authenticated = false;
        }
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        // if ( AuthUtils.isTokenExpired(this.expiryDate) )
        // {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        // return this.signInUsingToken();
        return of(true);
    }
}
